"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
const axios_1 = __importDefault(require("axios"));
const marketing_utils_1 = require("@swyftx/marketing-utils");
const constants_1 = require("../../constants");
const errors_1 = require("../../errors");
const serialization_1 = require("../../serialization");
class Client {
    constructor(options) {
        this.client = axios_1.default.create({
            baseURL: `${options.serviceUrl}${options.servicePrefix}`,
            timeout: options.requestTimeout,
            validateStatus: () => true,
            withCredentials: options.withCredentials,
            headers: options.headers,
        });
    }
    /**
     * Asset Reward Promotions
     */
    async getAllAssetRewards(options = {}) {
        const assetRewards = [];
        let page;
        while (page === undefined || page.currentPage < page.totalPages) {
            page = await this.getAssetRewards({
                ...options,
                pageNumber: page === undefined ? 1 : page.currentPage + 1,
                itemsPerPage: constants_1.MAX_ITEMS_PER_PAGE,
            });
            assetRewards.push(...page.assetRewards);
        }
        return assetRewards;
    }
    async getAssetRewards(options) {
        const { data, status } = await marketing_utils_1.http.apiGet(this.client, `/asset-rewards`, options);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not get asset reward promotions`, data);
        return (0, serialization_1.parseAssetRewardsPage)(data);
    }
    async getFirstAssetReward(options) {
        const page = await this.getAssetRewards({
            ...options,
            pageNumber: 1,
            itemsPerPage: 1,
        });
        return page.assetRewards.length ? page.assetRewards[0] : null;
    }
    async getAssetRewardById(id) {
        const { data, status } = await marketing_utils_1.http.apiGet(this.client, `/asset-rewards/${id}`);
        if (marketing_utils_1.http.isHttpNotFound(status))
            return null;
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not get asset reward promotion with ID ${id}`, data);
        return (0, serialization_1.parseAssetReward)(data);
    }
    async createAssetReward(options) {
        const { data, status } = await marketing_utils_1.http.apiPost(this.client, `/asset-rewards`, options);
        if (marketing_utils_1.http.isHttpConflict(status))
            throw new errors_1.ConflictingPromoCodeError(`Asset reward promotion with code '${options.promoCode}' already exists`);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not create asset reward promotion`, data);
        return (0, serialization_1.parseAssetReward)(data);
    }
    async updateAssetRewardById(id, options) {
        const { data, status } = await marketing_utils_1.http.apiPatch(this.client, `/asset-rewards/${id}`, options);
        if (marketing_utils_1.http.isHttpNotFound(status))
            throw new errors_1.PromotionNotFoundError(`Asset reward promotion with ID ${id} not found`);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not update asset reward promotion with ID ${id}`, data);
        return (0, serialization_1.parseAssetReward)(data);
    }
    /**
     * Asset Reward Audits
     */
    async getAssetRewardAudits(options) {
        const { data, status } = await marketing_utils_1.http.apiGet(this.client, `/asset-rewards-audit`, options);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not get asset reward promotion audits`, data);
        return (0, serialization_1.parseAssetRewardAuditsPage)(data);
    }
    /**
     * Asset Redemptions
     */
    async getAllAssetRedemptions(options = {}) {
        const assetRedemptions = [];
        let page;
        while (page === undefined || page.currentPage < page.totalPages) {
            page = await this.getAssetRedemptions({
                ...options,
                pageNumber: page === undefined ? 1 : page.currentPage + 1,
                itemsPerPage: constants_1.MAX_ITEMS_PER_PAGE,
            });
            assetRedemptions.push(...page.assetRedemptions);
        }
        return assetRedemptions;
    }
    async getAssetRedemptions(options) {
        const { data, status } = await marketing_utils_1.http.apiGet(this.client, `/asset-redemptions`, options);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not get asset redemptions`, data);
        return (0, serialization_1.parseAssetRedemptionsPage)(data);
    }
    async getFirstAssetRedemption(options) {
        const page = await this.getAssetRedemptions({
            ...options,
            pageNumber: 1,
            itemsPerPage: 1,
        });
        return page.assetRedemptions.length ? page.assetRedemptions[0] : null;
    }
    async createAssetRedemption(options) {
        const { data, status } = await marketing_utils_1.http.apiPost(this.client, `/asset-redemptions`, options);
        if (marketing_utils_1.http.isHttpNotFound(status))
            throw new errors_1.PromotionNotFoundError(`No live asset reward promotion with ID ${options.assetRewardId} found`);
        if (marketing_utils_1.http.isHttpConflict(status))
            throw new errors_1.ConflictingRedemptionError(`User already opted in to asset reward promotion ${options.assetRewardId}`);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not opt user in to asset reward promotion ${options.assetRewardId}`, data);
        return (0, serialization_1.parseAssetRedemption)(data);
    }
    async updateAssetRedemptionProgress(options) {
        const { data, status } = await marketing_utils_1.http.apiPost(this.client, `/asset-redemptions/progress`, options);
        if (marketing_utils_1.http.isHttpNotFound(status))
            return null;
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not update asset redemption progress for user ${options.userId} with trigger ${options.trigger}`, data);
        return (0, serialization_1.parseAssetRedemption)(data);
    }
    async setAssetRedemptionPaid(id, options) {
        const { data, status } = await marketing_utils_1.http.apiPost(this.client, `/asset-redemptions/${id}/paid`, options);
        if (marketing_utils_1.http.isHttpNotFound(status))
            throw new errors_1.RedemptionNotFoundError(`Asset redemption with ID ${id} not found`);
        if (!marketing_utils_1.http.isHttpSuccess(status))
            throw new errors_1.ServerError(`Could not set paid status for asset redemption with ID ${id}`, data);
        return (0, serialization_1.parseAssetRedemption)(data);
    }
}
exports.Client = Client;
//# sourceMappingURL=client.js.map